// Document type enum
export enum DocumentType {
  SYLLABUS = 'syllabus',
  EXAM = 'exam',
  ASSIGNMENT = 'assignment',
  NOTES = 'notes',
  OTHER = 'other',
  CHAT_IMAGE = 'chat_image'
}

// Document metadata for upload
export interface DocumentMetadata {
  filename: string;
  fileSize: number;
  mimeType: string;
  courseId?: string;
  documentType?: DocumentType;
}

// Upload request/response types
export interface UploadRequest {
  metadata: DocumentMetadata;
}

export interface PresignedUrlResponse {
  documentId: string;      // Document ID in the system
  uploadUrl: string;       // R2 endpoint URL for upload
  uploadFields: Record<string, string>;  // Form fields (empty for PUT)
  expiresAt: string;       // ISO timestamp when URL expires
  method?: 'POST' | 'PUT'; // HTTP method to use (defaults to POST)
  headers?: Record<string, string>; // Headers to include (for PUT)
}

// Processing status enum
export enum ProcessingStatus {
  PENDING = 'pending',
  QUEUED = 'queued',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  NOT_PROCESSABLE = 'not_processable'
}

// Processing result types
export interface ProcessingProgress {
  stage: 'queued' | 'extracting' | 'analyzing' | 'saving';
  percentage: number;
}

export interface ProcessingResults {
  textExtracted: boolean;
  wordCount: number;
  analysisComplete: boolean;
}

export interface AIInsights {
  documentType: string;
  summary?: string;
  keyDates?: Array<{ date: string; description: string }>;
  topics?: string[];
  difficulty?: number;
  recommendations?: string[];
  extractedInfo?: Record<string, any>;
}

// Document model
export interface Document {
  doc_id: number;
  user_id: number;
  course_id?: number;
  original_filename: string;
  file_type: string;
  file_size: number;
  mime_type: string;
  document_type?: DocumentType;
  uploaded_at: string;
  last_accessed_at?: string;
  r2_etag?: string;
  is_deleted?: boolean;
  // Processing fields
  processing_status?: ProcessingStatus;
  processing_started_at?: string;
  processing_completed_at?: string;
  processing_error?: string;
  extracted_text?: string;
  word_count?: number;
  ai_insights?: AIInsights;
  processing_cost?: number;
}

// API Response types
export interface UploadCompleteResponse {
  success: boolean;
  document: Document;
}

export interface DocumentListResponse {
  documents: Document[];
  total: number;
  skip: number;
  limit: number;
}

export interface ViewUrlResponse {
  url: string;
  expires_in: number;
}

export interface QuotaInfo {
  usage: number;        // Bytes for precise calculations
  limit: number;        // Bytes for precise calculations
  usageMB: number;      // MB for user display
  limitMB: number;      // MB for user display
  percentUsed: number;  // Percentage for progress bars
}

export interface DocumentStats {
  total_documents: number;
  total_size_bytes: number;  // For precise calculations
  total_size_mb: number;     // For user display
  by_type: {
    [key: string]: {
      count: number;
      size: number;  // Bytes
    };
  };
}

export interface DeleteResponse {
  success: boolean;
  message: string;
}

// Batch delete types
export interface BatchDeleteRequest {
  document_ids: string[];
}

export interface BatchDeleteResult {
  document_id: string;
  success: boolean;
  error?: string;
}

export interface BatchDeleteResponse {
  success: boolean;
  results: BatchDeleteResult[];
  totalRequested: number;
  totalDeleted: number;
  totalFailed: number;
}

// Processing status API response
export interface ProcessingStatusResponse {
  documentId: string;
  status: ProcessingStatus;
  progress?: ProcessingProgress;
  results?: ProcessingResults;
  error?: string;
  updatedAt: string;
}

// Reprocess API request/response
export interface ReprocessRequest {
  operations?: string[];
  force?: boolean;
}

export interface ReprocessResponse {
  jobId: string;
  status: 'queued';
  estimatedCost: number;
}

// Upload queue item for tracking uploads
export interface UploadQueueItem {
  id: string;
  file: File;
  metadata?: DocumentMetadata;
  progress: number;
  status: 'queued' | 'uploading' | 'completed' | 'error';
  error?: string;
  retryCount?: number;
  speed?: number;
  timeRemaining?: number;
  documentId?: string; // Document ID after upload
  processingStatus?: string; // Processing status after upload
  processingError?: string; // Processing error if any
}

// Document filters for list operations
export interface DocumentFilters {
  courseId?: string;
  documentType?: DocumentType;
  // Additional UI-only filters (applied client-side)
  fileTypes?: string[]; // e.g., ['pdf', 'image', 'png']
  search?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'date' | 'name' | 'size';
  sortOrder?: 'asc' | 'desc';
}

// File validation constants
export const SUPPORTED_FILE_TYPES = {
  PDF: ['.pdf'],
  IMAGES: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  DOCUMENTS: ['.doc', '.docx', '.txt', '.rtf'],
  SPREADSHEETS: ['.xls', '.xlsx', '.csv'],
  PRESENTATIONS: ['.ppt', '.pptx'],
  DATA: ['.json', '.xml', '.yaml', '.yml'],
  CODE: ['.py', '.js', '.ts', '.java', '.cpp', '.c', '.cs', '.rb', '.go', '.rs', 
         '.html', '.css', '.jsx', '.tsx', '.vue', '.php', '.swift', '.kt'],
  TEXT: ['.md', '.markdown', '.tex', '.latex']
} as const;

export const SUPPORTED_MIME_TYPES = [
  // Document formats
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/rtf',
  
  // Spreadsheet formats
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  
  // Presentation formats
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  
  // Image formats
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  
  // Data formats
  'application/json',
  'text/json',
  'application/xml',
  'text/xml',
  'application/x-yaml',
  'text/yaml',
  
  // Code and text formats
  'text/html',
  'text/css',
  'text/javascript',
  'application/javascript',
  'text/x-python',
  'text/x-java',
  'text/x-c',
  'text/x-cpp',
  'text/x-csharp',
  'text/x-ruby',
  'text/x-go',
  'text/x-rust',
  'text/x-typescript',
  'text/x-php',
  'text/x-swift',
  'text/x-kotlin',
  'text/markdown',
  'text/x-markdown',
  'application/x-latex',
  'text/x-tex'
];

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
export const DEFAULT_USER_QUOTA = 1024 * 1024 * 1024; // 1GB in bytes
